import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { AuthShell, SubmitBtn, ErrorNote } from "./AuthShell";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { user, reload, setActiveOrgId } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Lien d'invitation invalide.");
      return;
    }
    api
      .get(`/invitations/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((err) => setError(formatApiError(err.response?.data?.detail, err.message)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const accept = async () => {
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/invitations/accept", { token });
      await reload();
      if (data.org?.id) setActiveOrgId(data.org.id);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  const emailMismatch =
    user && invite && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <AuthShell
      title="Invitation"
      subtitle={invite ? `Vous êtes invité(e) à rejoindre ${invite.org_name}.` : "Vérification de l'invitation…"}
    >
      <div className="space-y-5" data-testid="accept-invite">
        <ErrorNote>{error}</ErrorNote>

        {invite && (
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Organisation</p>
            <p className="mt-1 font-heading text-lg font-bold text-[#1E3D2B]" data-testid="invite-org">
              {invite.org_name}
            </p>
            <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4 text-sm">
              <span className="text-slate-500">Rôle proposé</span>
              <span className="rounded-full bg-[#ECF3EC] px-2.5 py-0.5 text-xs font-semibold text-[#3E7256]" data-testid="invite-role">
                {invite.role}
              </span>
            </div>
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="text-slate-500">Adresse invitée</span>
              <span className="font-medium text-slate-700" data-testid="invite-email">{invite.email}</span>
            </div>
          </div>
        )}

        {!user ? (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Connectez-vous ou créez un compte avec <strong>{invite?.email}</strong> pour accepter.
            </p>
            <div className="flex gap-3">
              <Link
                to="/login"
                className="flex h-11 flex-1 items-center justify-center rounded-lg bg-[#1E3D2B] text-sm font-semibold text-white hover:bg-[#2C5540]"
              >
                Se connecter
              </Link>
              <Link
                to="/register"
                className="flex h-11 flex-1 items-center justify-center rounded-lg border border-slate-200 text-sm font-semibold text-[#1E3D2B] hover:bg-slate-50"
              >
                Créer un compte
              </Link>
            </div>
          </div>
        ) : emailMismatch ? (
          <ErrorNote>
            Vous êtes connecté(e) en tant que {user.email}, mais cette invitation est destinée à{" "}
            {invite.email}. Déconnectez-vous puis reconnectez-vous avec la bonne adresse.
          </ErrorNote>
        ) : (
          invite && (
            <SubmitBtn loading={loading} onClick={accept} data-testid="accept-invite-btn">
              Rejoindre {invite.org_name}
            </SubmitBtn>
          )
        )}
      </div>
    </AuthShell>
  );
}
